import * as Schema from "effect/Schema";
import { scopeProjectRef } from "@t3tools/client-runtime/environment";
import { safeErrorLogAttributes } from "@t3tools/client-runtime/errors";
import {
  isAtomCommandInterrupted,
  squashAtomCommandFailure,
} from "@t3tools/client-runtime/state/runtime";
import {
  ChevronDownIcon,
  FolderIcon,
  FolderPlusIcon,
  MoreHorizontalIcon,
  PencilIcon,
  SettingsIcon,
  Trash2Icon,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";

import { openCommandPalette } from "../../commandPaletteBus";
import { useComposerDraftStore } from "../../composerDraftStore";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import { readLocalApi } from "../../localApi";
import { cn } from "~/lib/utils";
import { projectEnvironment } from "../../state/projects";
import { useThreadShells } from "../../state/entities";
import { useAtomCommand } from "../../state/use-atom-command";
import { useI18n } from "~/i18n";
import type { SidebarProjectSnapshot } from "../../sidebarProjectGrouping";
import { ProjectFavicon } from "../ProjectFavicon";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Menu, MenuItem, MenuPopup, MenuTrigger } from "../ui/menu";
import { stackedThreadToast, toastManager } from "../ui/toast";

const WORKSPACE_SHELF_EXPANDED_KEY = "t3code:sidebar-v2:workspace-expanded";

type SidebarWorkspaceScopeProps = {
  readonly projectGroups: ReadonlyArray<SidebarProjectSnapshot>;
  readonly scopedProjectGroup: SidebarProjectSnapshot | null;
  readonly projectScopeKey: string | null;
  readonly onProjectScopeChange: (projectKey: string) => void;
  readonly onOpenSettings: (projectGroup: SidebarProjectSnapshot) => void;
};

export function SidebarWorkspaceScope({
  projectGroups,
  scopedProjectGroup,
  projectScopeKey,
  onProjectScopeChange,
  onOpenSettings,
}: SidebarWorkspaceScopeProps) {
  const { t } = useI18n();
  const threads = useThreadShells();
  const deleteProject = useAtomCommand(projectEnvironment.delete, { reportFailure: false });
  const updateProject = useAtomCommand(projectEnvironment.update, { reportFailure: false });
  const [expanded, setExpanded] = useLocalStorage(
    WORKSPACE_SHELF_EXPANDED_KEY,
    true,
    Schema.Boolean,
  );
  const [renameTarget, setRenameTarget] = useState<SidebarProjectSnapshot | null>(null);
  const [renameTitle, setRenameTitle] = useState("");

  const threadCountByProjectRef = useMemo(() => {
    const counts = new Map<string, number>();
    for (const thread of threads) {
      const key = `${thread.environmentId}:${thread.projectId}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [threads]);

  const threadCountForGroup = useCallback(
    (group: SidebarProjectSnapshot) =>
      group.memberProjectRefs.reduce(
        (sum, ref) =>
          sum + (threadCountByProjectRef.get(`${ref.environmentId}:${ref.projectId}`) ?? 0),
        0,
      ),
    [threadCountByProjectRef],
  );

  const toggleExpanded = useCallback(() => setExpanded((value) => !value), [setExpanded]);

  const openAddWorkspace = useCallback(() => {
    openCommandPalette({ open: "add-project" });
  }, []);

  const openRenameDialog = useCallback((group: SidebarProjectSnapshot) => {
    setRenameTarget(group);
    setRenameTitle(group.displayName);
  }, []);

  const closeRenameDialog = useCallback(() => {
    setRenameTarget(null);
    setRenameTitle("");
  }, []);

  const submitRename = useCallback(async () => {
    if (!renameTarget) return;
    const title = renameTitle.trim();
    if (!title) {
      toastManager.add({ type: "warning", title: t("sidebar.workspaceRenameEmpty") });
      return;
    }
    if (title === renameTarget.displayName) {
      closeRenameDialog();
      return;
    }

    const results = await Promise.all(
      renameTarget.memberProjects.map((member) =>
        updateProject({
          environmentId: member.environmentId,
          input: { projectId: member.id, title },
        }),
      ),
    );
    const failure = results.find((result) => result._tag === "Failure");
    if (failure?._tag === "Failure" && !isAtomCommandInterrupted(failure)) {
      const error = squashAtomCommandFailure(failure);
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title: t("sidebar.workspaceRenameFailed"),
          description: error instanceof Error ? error.message : undefined,
        }),
      );
      return;
    }
    closeRenameDialog();
  }, [closeRenameDialog, renameTarget, renameTitle, t, updateProject]);

  const removeWorkspaceMembers = useCallback(
    async (group: SidebarProjectSnapshot, options: { force?: boolean } = {}) => {
      for (const member of group.memberProjects) {
        const result = await deleteProject({
          environmentId: member.environmentId,
          input: {
            projectId: member.id,
            ...(options.force === true ? { force: true } : {}),
          },
        });
        if (result._tag === "Failure") {
          return result;
        }
        const memberProjectRef = scopeProjectRef(member.environmentId, member.id);
        const draftStore = useComposerDraftStore.getState();
        const projectDraftThread = draftStore.getDraftThreadByProjectRef(memberProjectRef);
        if (projectDraftThread) {
          draftStore.clearDraftThread(projectDraftThread.draftId);
        }
        draftStore.clearProjectDraftThreadId(memberProjectRef);
      }
      return { _tag: "Success" as const };
    },
    [deleteProject],
  );

  const handleRemoveWorkspace = useCallback(
    async (group: SidebarProjectSnapshot) => {
      const api = readLocalApi();
      if (!api) return;

      const threadCount = threadCountForGroup(group);
      const lines =
        threadCount > 0
          ? [
              t("sidebar.removeWorkspaceWithThreads", {
                name: group.displayName,
                count: threadCount,
              }),
              t("sidebar.removeWorkspaceThreadsWarning"),
              t("sidebar.removeWorkspaceDiskWarning"),
              t("sidebar.removeWorkspaceUndoWarning"),
            ]
          : [
              t("sidebar.removeWorkspaceConfirm", { name: group.displayName }),
              t("sidebar.removeWorkspaceDiskWarning"),
            ];

      if (threadCount > 0) {
        const warningToastId = toastManager.add(
          stackedThreadToast({
            type: "warning",
            title: t("sidebar.workspaceNotEmpty"),
            description: t("sidebar.workspaceNotEmptyDescription"),
            actionVariant: "destructive",
            actionProps: {
              children: t("sidebar.removeAnyway"),
              onClick: () => {
                void (async () => {
                  toastManager.close(warningToastId);
                  await new Promise<void>((resolve) => {
                    window.setTimeout(resolve, 180);
                  });
                  const confirmed = await api.dialogs.confirm(lines.join("\n"), {
                    variant: "destructive",
                  });
                  if (!confirmed) return;
                  const result = await removeWorkspaceMembers(group, { force: true });
                  if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
                    const error = squashAtomCommandFailure(result);
                    toastManager.add(
                      stackedThreadToast({
                        type: "error",
                        title: t("sidebar.workspaceRemoveFailed", { name: group.displayName }),
                        description: error instanceof Error ? error.message : undefined,
                      }),
                    );
                  }
                })();
              },
            },
          }),
        );
        return;
      }

      const confirmed = await api.dialogs.confirm(lines.join("\n"), { variant: "destructive" });
      if (!confirmed) return;

      const result = await removeWorkspaceMembers(group);
      if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
        const error = squashAtomCommandFailure(result);
        console.error("Failed to remove workspace", safeErrorLogAttributes(error));
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: t("sidebar.workspaceRemoveFailed", { name: group.displayName }),
            description: error instanceof Error ? error.message : undefined,
          }),
        );
      }
    },
    [removeWorkspaceMembers, t, threadCountForGroup],
  );

  if (projectGroups.length === 0 || scopedProjectGroup === null) {
    return null;
  }

  const activeKey = projectScopeKey ?? projectGroups[0]?.projectKey ?? "";

  return (
    <>
      <div className="flex flex-col gap-px">
        <button
          type="button"
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-label={t("sidebar.filterByWorkspace")}
          data-testid="sidebar-workspace-shelf-toggle"
          className="mb-1 mt-3 flex w-full cursor-pointer items-center gap-2 px-2.5 text-left"
        >
          {!expanded ? (
            <span className="min-w-0 truncate text-xs font-medium text-muted-foreground/50">
              {scopedProjectGroup.displayName}
            </span>
          ) : null}
          <span className="h-px flex-1 bg-sidebar-border/60" />
          <ChevronDownIcon
            aria-hidden
            className={cn(
              "size-3 shrink-0 text-muted-foreground/50 transition-transform",
              expanded && "rotate-180",
            )}
          />
        </button>

        {expanded ? (
          <div className="flex flex-col gap-px">
            {projectGroups.map((group) => {
              const isActive = group.projectKey === activeKey;
              return (
                <div
                  key={group.projectKey}
                  className={cn(
                    "group/workspace-row flex h-9 items-center gap-1 rounded-md pe-1 ps-2",
                    isActive
                      ? "bg-sidebar-row-hover text-sidebar-foreground"
                      : "text-sidebar-muted-foreground hover:bg-sidebar-row-hover hover:text-sidebar-foreground",
                  )}
                >
                  <button
                    type="button"
                    data-testid={`sidebar-workspace-row-${group.projectKey}`}
                    onClick={() => onProjectScopeChange(group.projectKey)}
                    className="flex min-w-0 flex-1 cursor-pointer items-center gap-2.5 text-left"
                  >
                    <WorkspaceAvatar project={group} />
                    <span className="min-w-0 flex-1 truncate text-sm">{group.displayName}</span>
                  </button>
                  <WorkspaceRowMenu
                    onAddWorkspace={openAddWorkspace}
                    onRename={() => openRenameDialog(group)}
                    onOpenSettings={() => onOpenSettings(group)}
                    onRemove={() => void handleRemoveWorkspace(group)}
                  />
                </div>
              );
            })}
          </div>
        ) : null}
      </div>

      <Dialog
        open={renameTarget !== null}
        onOpenChange={(open) => {
          if (!open) closeRenameDialog();
        }}
      >
        <DialogPopup className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t("sidebar.renameWorkspaceTitle")}</DialogTitle>
            <DialogDescription>{t("sidebar.renameWorkspaceDescription")}</DialogDescription>
          </DialogHeader>
          <DialogPanel className="space-y-4">
            <div className="grid gap-1.5">
              <span className="text-xs font-medium text-foreground">
                {t("sidebar.workspaceTitleLabel")}
              </span>
              <Input
                aria-label={t("sidebar.workspaceTitleLabel")}
                value={renameTitle}
                onChange={(event) => setRenameTitle(event.currentTarget.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void submitRename();
                  }
                }}
              />
            </div>
          </DialogPanel>
          <DialogFooter>
            <Button variant="outline" onClick={closeRenameDialog}>
              {t("common.close")}
            </Button>
            <Button onClick={() => void submitRename()}>{t("common.done")}</Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>
    </>
  );
}

function WorkspaceRowMenu({
  onAddWorkspace,
  onRename,
  onOpenSettings,
  onRemove,
}: {
  readonly onAddWorkspace: () => void;
  readonly onRename: () => void;
  readonly onOpenSettings: () => void;
  readonly onRemove: () => void;
}) {
  const { t } = useI18n();

  return (
    <Menu>
      <MenuTrigger
        render={
          <Button
            type="button"
            size="icon-xs"
            variant="ghost-muted"
            aria-label={t("sidebar.workspaceActions")}
            data-testid="sidebar-workspace-row-menu"
            className="size-7 shrink-0 opacity-0 transition-opacity group-hover/workspace-row:opacity-100 group-focus-within/workspace-row:opacity-100 [--control-icon-color:currentColor] text-icon-muted"
            onClick={(event) => event.stopPropagation()}
          />
        }
      >
        <MoreHorizontalIcon className="size-4" />
      </MenuTrigger>
      <MenuPopup align="end" className="min-w-44">
        <MenuItem className="gap-2" onClick={onAddWorkspace}>
          <FolderPlusIcon className="size-4 shrink-0" />
          {t("sidebar.addWorkspace")}
        </MenuItem>
        <MenuItem className="gap-2" onClick={onRename}>
          <PencilIcon className="size-4 shrink-0" />
          {t("sidebar.renameWorkspace")}
        </MenuItem>
        <MenuItem className="gap-2" onClick={onOpenSettings}>
          <SettingsIcon className="size-4 shrink-0" />
          {t("sidebar.workspaceSettingsAction")}
        </MenuItem>
        <MenuItem variant="destructive" className="gap-2" onClick={onRemove}>
          <Trash2Icon className="size-4 shrink-0" />
          {t("sidebar.removeWorkspace")}
        </MenuItem>
      </MenuPopup>
    </Menu>
  );
}

function WorkspaceAvatar({ project }: { readonly project: SidebarProjectSnapshot }) {
  return (
    <ProjectFavicon
      environmentId={project.environmentId}
      cwd={project.workspaceRoot}
      faviconPath={project.faviconPath}
      className="size-4 shrink-0"
      fallbackIcon={FolderIcon}
    />
  );
}
